import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { deviceApi } from '../services/deviceApi';
import { extractHardwareChanges, extractSoftwareChanges } from '../utils/deviceVersionUtils';

export function useDeviceDetail(deviceId, { cachedDevice = null, isSupport = false, roleReady = true } = {}) {
  const cachedRef = useRef(cachedDevice);
  const [device, setDevice] = useState(() => cachedRef.current);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(() => !cachedRef.current);
  const [historyLoaded, setHistoryLoaded] = useState(!isSupport);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!roleReady) return undefined;

    if (!deviceId) {
      setError('Device not found.');
      setLoading(false);
      setHistoryLoaded(true);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setError('');
      try {
        const fallback = cachedRef.current;
        const [detail, historyRows] = await Promise.all([
          deviceApi.getDeviceDetail(deviceId).catch(() => fallback),
          isSupport ? deviceApi.getDeviceHistory(deviceId) : Promise.resolve([]),
        ]);

        if (cancelled) return;

        if (!detail) {
          setError('Device not found.');
          return;
        }

        setDevice(detail);
        setHistory(historyRows);
      } catch (err) {
        if (cancelled) return;
        if (cachedRef.current) {
          setDevice(cachedRef.current);
        } else {
          setError(err?.response?.data?.error || err.message || 'Failed to load device details.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setHistoryLoaded(true);
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [deviceId, isSupport, roleReady]);

  const reloadHistory = useCallback(async () => {
    if (!isSupport || !deviceId) return;
    const historyRows = await deviceApi.getDeviceHistory(deviceId);
    setHistory(historyRows);
    setHistoryLoaded(true);
  }, [deviceId, isSupport]);

  const hardwareChanges = useMemo(() => extractHardwareChanges(history), [history]);
  const softwareChanges = useMemo(() => extractSoftwareChanges(history), [history]);

  return {
    device,
    setDevice,
    history,
    loading,
    historyLoaded,
    error,
    hardwareChanges,
    softwareChanges,
    reloadHistory,
  };
}
